import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Typography, Container, Box, Pagination } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../components/LoaderContainer";
import CustomButton from "../../components/Custom/CustomButton";
import LabTestIcon from "../../assets/Icon/LabTestIcon.png";
import CustomCard from "../../components/Famer/GenericCard";
import { useAlert } from "../../components/Alert";
import { useNavigate } from "react-router-dom";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import { isLoginFromSoilApp } from "../../globals/authentication";

const SoilTesting = () => {
  const [reports, setReports] = useState([]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const { showAlert } = useAlert();
  const [pagination, setPagination] = useState({
    page: 1,
    total_pages: 0,
    total_objects: 0,
  });
  const navigate = useNavigate();
  const isSoilAppLogin = isLoginFromSoilApp();

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = (page = 1) => {
    setLoading(true);
    let url = `/soil-testing/test-report/?page=${page}`;
    if (isSoilAppLogin) {
      url = `${url}&is_from_soil_app=true`;
    }
    api
      .get(url)
      .then((res) => {
        if (isNotNullOrEmptyArray(res.data.results)) {
          setReports(res.data.results);
          setPagination({
            page: res.data.current_page_number,
            total_pages: res.data.total_pages,
            total_objects: res.data.total_objects,
          });
        } else {
          setReports([]);
        }
      })
      .catch((err) => {
        console.log(err);
        showAlert(
          "error",
          err.response?.data?.non_field_errors?.[0] ||
            "Failed to load Soil Test Reports"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleAddLabTest = () => {
    navigate("/farmer/soil-testing/create");
  };

  const handleView = (report_id) => {
    navigate(`/soil-test-report/${report_id}`);
  };

  const handlePageChange = (event, value) => {
    loadReports(value);
  };

  const hasReports = isNotNullOrEmptyArray(reports);

  return (
    <>
      <Helmet>
        <title>Soil Testing - Farmapp</title>
      </Helmet>
      {!isSoilAppLogin && <CustomAppBar />}
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          sx={{
            minHeight: "calc(100vh - 64px)",
            display: "flex",
            flexDirection: "column",
            py: 2,
          }}
        >
          {hasReports ? (
            <>
              {!isSoilAppLogin && (
                <Box sx={{ mb: 2 }}>
                  <CustomButton onClick={handleAddLabTest} fullWidth>
                    {t("Add Soil Test")}
                  </CustomButton>
                </Box>
              )}
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontSize: "24px",
                  fontWeight: 700,
                }}
              >
                {t("Soil Test Reports")}
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  width: "100%",
                  mb: 2,
                }}
              >
                {reports.map((report) => (
                  <CustomCard
                    key={report}
                    id={report.id}
                    t={t}
                    data={{
                      [t("Soil Test ID")]: report.id,
                      [t("Farm ID")]: report.farm_land,
                      [t("Farmer Name")]:
                        report.farmer_details?.farmerName || "NA",
                      [t("Crop Name")]: report.farmer_details?.cropName || "NA",
                      [t("Nitrogen")]: report.nitrogen,
                      [t("Phosphorus")]: report.phosphorus,
                      [t("Potassium")]: report.potassium,
                      [t("Report Date")]: (report.report_time || "").split(
                        "T"
                      )[0],
                    }}
                    handlePrimaryButton={handleView}
                    disableChange
                  />
                ))}
              </Box>

              {pagination.total_pages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={pagination.total_pages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    disabled={loading}
                  />
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                textAlign: "center",
              }}
            >
              <img
                src={LabTestIcon}
                alt="My Crops Icon"
                style={{
                  width: "127px",
                  height: "127px",
                  marginBottom: "10px",
                }}
              />

              {!isSoilAppLogin && (
                <CustomButton onClick={handleAddLabTest} sx={{ mb: 2 }}>
                  {t("Add Soil Test")}
                </CustomButton>
              )}

              <Typography variant="subtitle1">
                {t("Your Soil Tests will be shown here once you add them.")}
              </Typography>
            </Box>
          )}
        </Container>
      </LoaderContainer>
    </>
  );
};

export default SoilTesting;
